import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { CatalogHeader } from '../CatalogHeader/CatalogHeader';
import { EntityView } from '../EntityView/EntityView';
import { EntityFiltersAndSearch } from '../EntityFiltersAndSearch/EntityFiltersAndSearch';
import { blue } from '@mercedes-benz/mui5-theme';
import { makeStyles } from '@mui/styles';
import { BASE_ROUTE } from '../../constants';
import Container from '@mui/material/Container';

const ALL_ROUTE = `${BASE_ROUTE}/all`;
const OWNED_ROUTE = `${BASE_ROUTE}/owned`;
const FAVOURITES_ROUTE = `${BASE_ROUTE}/favourites`;

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`catalog-tabpanel-${index}`}
      aria-labelledby={`catalog-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `catalog-tab-${index}`,
    'aria-controls': `catalog-tabpanel-${index}`,
  };
}

const useStyles = makeStyles(theme => ({
  tabsWrapper: {
    marginTop: '24px',
    borderBottom:
      theme.palette.mode === 'dark' ? '1px solid #2A2A2A' : '1px solid #D7D7D7',
  },
}));

export const CatalogHome = () => {
  const location = useLocation();

  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [entityViewComponent] = useState(<EntityView />);

  const classes = useStyles();

  useEffect(() => {
    const navigateByUserFilter = () => {
      const searchParams = new URLSearchParams(location.search);
      const userParam = searchParams.get('filters[user]');

      if (userParam === 'all') {
        setValue(1);
        navigate(ALL_ROUTE);
      } else if (userParam === 'starred') {
        setValue(2);
        navigate(FAVOURITES_ROUTE);
      } else {
        setValue(0);
        navigate(OWNED_ROUTE);
      }
    };

    switch (location.pathname) {
      case OWNED_ROUTE:
        setValue(0);
        break;
      case ALL_ROUTE:
        setValue(1);
        break;
      case FAVOURITES_ROUTE:
        setValue(2);
        break;
      default:
        navigateByUserFilter();
        break;
    }
  }, [location, navigate]);

  const handleChange = (_: SyntheticEvent, newValue: number) => {
    switch (newValue) {
      case 0:
        navigate(OWNED_ROUTE);
        break;
      case 1:
        navigate(ALL_ROUTE);
        break;
      case 2:
        navigate(FAVOURITES_ROUTE);
        break;
      default:
        navigate(OWNED_ROUTE);
        break;
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ margin: '40px' }}>
        <CatalogHeader />
        <Box className={classes.tabsWrapper}>
          <EntityFiltersAndSearch />
          <Tabs
            TabIndicatorProps={{
              style: {
                backgroundColor: blue[50],
              },
            }}
            value={value}
            onChange={handleChange}
            aria-label="catalog-tabs"
          >
            <Tab
              data-testid="my-entities-tab"
              label="My entities"
              style={{
                color: value === 0 ? blue[50] : 'inherit',
              }}
              {...a11yProps(0)}
            />
            <Tab
              data-testid="all-tab"
              label="All"
              style={{
                color: value === 1 ? blue[50] : 'inherit',
              }}
              {...a11yProps(1)}
            />
            <Tab
              data-testid="favourites-tab"
              label="Favourites"
              style={{
                color: value === 2 ? blue[50] : 'inherit',
              }}
              {...a11yProps(2)}
            />
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>
          {entityViewComponent}
        </TabPanel>
        <TabPanel value={value} index={1}>
          {entityViewComponent}
        </TabPanel>
        <TabPanel value={value} index={2}>
          {entityViewComponent}
        </TabPanel>
      </Box>
    </Container>
  );
};
